/*
 * Pestaña Importaciones — Semáforo: DO sin levante y con levante,
 * réplica del tab-importaciones del HTML original.
 */
import { useMemo } from "react";
import { Description, Title } from "codekit";

import Panelbox from "./Panelbox";
import Semcards from "./Semcards";
import Simtable, { Dotspanx, Valspanx } from "./Simtable";
import Stakbars from "./Stakbars";
import { bucketColors, bucketConLevante, bucketSinLevante, fmtCOP, htmlTheme, truncate } from "./utils/carthelp";

const IMP = "IMPORTACION";
const FUNNEL_KEYS = ["GERENCIA", "NOMBRE", "DIRECTOR DE CUENTA"];

/**
 * Agrupa filas en rangos de semáforo y por cliente
 * @param {Array} mRows Filas de importación de un segmento
 * @param {Function} bucketFn Clasificador de rango
 * @param {string} sDiasKey Campo de días a evaluar
 * @returns {Object} { groups, byClient }
 */
const buildSegment = (mRows, bucketFn, sDiasKey) => {
  const mGroups = {
    verde: { count: 0, valor: 0, rows: [] },
    amarillo: { count: 0, valor: 0, rows: [] },
    rojo: { count: 0, valor: 0, rows: [] },
  };
  const mByClient = {};
  mRows.forEach((r) => {
    const nD = r[sDiasKey];
    if (nD === null) return;
    const sB = bucketFn(nD);
    mGroups[sB].count++;
    mGroups[sB].valor += r.VALOR;
    mGroups[sB].rows.push(r);
    if (!mByClient[r.NOMBRE]) mByClient[r.NOMBRE] = { verde: 0, amarillo: 0, rojo: 0, total: 0 };
    mByClient[r.NOMBRE][sB] += r.VALOR;
    mByClient[r.NOMBRE].total += r.VALOR;
  });
  const mTop = Object.entries(mByClient)
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 10)
    .map(([sLabel, v]) => ({ label: truncate(sLabel, 28), raw: sLabel, ...v }));
  return { groups: mGroups, byClient: mTop };
};

/**
 * Construye las columnas de la tabla de un segmento
 * @param {string} sDiasTitle Título de la columna de días
 * @param {string} sDiasKey Campo de días
 * @param {Function} bucketFn Clasificador de rango
 * @returns {Array} Columnas de Simtable
 */
const buildColumns = (sDiasTitle, sDiasKey, bucketFn) => [
  { key: "DO", title: "DO" },
  { key: "NOMBRE", title: "Cliente" },
  { key: "DIRECTOR DE CUENTA", title: "Director de Cuenta" },
  {
    key: sDiasKey,
    title: sDiasTitle,
    render: (r) => (
      <span>
        <Dotspanx color={bucketColors[bucketFn(r[sDiasKey])]} />
        {r[sDiasKey]}
      </span>
    ),
  },
  { key: "VALOR", title: "Valor", render: (r) => <Valspanx value={r.VALOR} text={fmtCOP(r.VALOR)} /> },
];

/**
 * Renderiza el hint con puntos de color de un segmento
 * @param {Array} mCortes Tramos [texto verde, texto amarillo, texto rojo]
 * @returns {React.ReactNode}
 */
const buildHint = (mCortes) => (
  <span>
    Corte: <Dotspanx color={bucketColors.verde} />
    {mCortes[0]} · <Dotspanx color={bucketColors.amarillo} />
    {mCortes[1]} · <Dotspanx color={bucketColors.rojo} />
    {mCortes[2]}
  </span>
);

/**
 * Renderiza un bloque de segmento (sin o con levante)
 * @param {Object} props Propiedades del bloque
 */
const Segment = ({ title, hint, chartTitle, segment, tableRows, columns, breadcrumb, onFunnel, isFirst = false }) => (
  <>
    <div
      style={
        isFirst
          ? { color: htmlTheme.navy2, margin: "0 0 10px" }
          : { color: htmlTheme.navy2, margin: "26px 0 10px", paddingTop: 16, borderTop: `2px solid ${htmlTheme.border}` }
      }
    >
      <Title variant="title-2">{title}</Title>
    </div>
    <div style={{ color: htmlTheme.muted, marginBottom: 10 }}>
      <Description variant="description-3">{hint}</Description>
    </div>
    <Semcards groups={segment.groups} onCardClick={(sK) => onFunnel(segment.groups[sK].rows, FUNNEL_KEYS, [`${breadcrumb} · ${sK}`])} />
    <Panelbox title={chartTitle} hint="De mayor a menor, coloreado por rango de días · click en un cliente para ver el detalle">
      <Stakbars
        data={segment.byClient}
        legend={["Verde", "Amarillo", "Rojo"]}
        height={400}
        onItemClick={(m) =>
          onFunnel(
            [...segment.groups.verde.rows, ...segment.groups.amarillo.rows, ...segment.groups.rojo.rows].filter((r) => r.NOMBRE === m.raw),
            ["TIPO DE OPERACIÓN", "DIRECTOR DE CUENTA"],
            [breadcrumb, m.raw],
          )
        }
      />
    </Panelbox>
    <div style={{ marginBottom: 16 }}>
      <Simtable columns={columns} rows={tableRows} />
    </div>
  </>
);

/**
 * Renderiza la pestaña Importaciones — Semáforo
 * @param {Object} props Propiedades del componente
 * @param {Array} props.data Datos de trabajo filtrados
 * @param {Function} props.onFunnel Abre el drill-down (scope, keys, breadcrumb)
 */
const Tabimpor = ({ data, onFunnel }) => {
  const mImp = useMemo(() => data.filter((r) => r["TIPO DE OPERACIÓN"] === IMP), [data]);
  const mSin = useMemo(() => mImp.filter((r) => !r._tieneLevante), [mImp]);
  const mCon = useMemo(() => mImp.filter((r) => r._tieneLevante), [mImp]);
  const mSegSin = useMemo(() => buildSegment(mSin, bucketSinLevante, "_dias"), [mSin]);
  const mSegCon = useMemo(() => buildSegment(mCon, bucketConLevante, "_diasLevante"), [mCon]);
  const mRowsSin = useMemo(
    () => mSin.filter((r) => r._dias !== null).sort((a, b) => b._dias - a._dias).slice(0, 50),
    [mSin],
  );
  const mRowsCon = useMemo(
    () => mCon.filter((r) => r._diasLevante !== null).sort((a, b) => b._diasLevante - a._diasLevante).slice(0, 50),
    [mCon],
  );

  return (
    <>
      <Segment
        title="🔴 DO sin levante (aún no inicia el reloj de facturación)"
        hint={buildHint(["0-7 días", "8-20 días", "+20 días desde creación"])}
        chartTitle="Clientes con Mayor Exposición — Sin Levante"
        segment={mSegSin}
        tableRows={mRowsSin}
        columns={buildColumns("Días desde creación", "_dias", bucketSinLevante)}
        breadcrumb="Sin levante"
        onFunnel={onFunnel}
        isFirst
      />
      <Segment
        title="⏱️ DO con levante ya emitido (ventana de gracia ~3 días para facturar)"
        hint={buildHint(["0-3 días", "4-7 días", "+7 días desde el levante"])}
        chartTitle="Clientes con Mayor Exposición — Con Levante"
        segment={mSegCon}
        tableRows={mRowsCon}
        columns={buildColumns("Días desde levante", "_diasLevante", bucketConLevante)}
        breadcrumb="Con levante"
        onFunnel={onFunnel}
      />
    </>
  );
};

export default Tabimpor;
